import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Form,
  InputGroup,
  Alert,
} from 'react-bootstrap';
import {
  ArrowLeft,
  FileEarmarkText,
  Search,
  SortAlphaDown,
  Calendar,
  Person,
  Envelope,
  Clock,
  ChatLeftText,
  ArrowClockwise,
  Briefcase,
  GeoAlt,
} from 'react-bootstrap-icons';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const Notes = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  // Removed projectInfo state since we can't fetch it
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  /* ================= FETCH NOTES ================= */
  const fetchNotes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/data/getBidNotesByProject/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setNotes(res.data.bidNotes || []);
        // Removed project info fetching since API doesn't exist
      }
    } catch (error) {
      console.error('Fetch notes error:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchNotes();
    }
  }, [projectId]);

  /* ================= FILTER & SORT ================= */
  const filteredNotes = notes
    .filter((note) => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          note.note?.toLowerCase().includes(searchLower) ||
          note.createdBy?.name?.toLowerCase().includes(searchLower) ||
          note.createdBy?.email?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'date':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        case 'author':
          aVal = a.createdBy?.name || '';
          bVal = b.createdBy?.name || '';
          break;
        default:
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  /* ================= FORMATTERS ================= */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return formatDate(dateString);
  };

  const getInitials = (name) => {
    if (!name) return 'NN';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading notes...</span>
      </div>
    );
  }

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <ArrowLeft className="me-2" />
                Back to Project List
              </Button>
              <h2 className="fw-bold mb-2">
                <FileEarmarkText className="me-3" />
                Project Notes
              </h2>
              <div className="d-flex align-items-center">
                <Badge bg="info" className="px-3 py-2 me-3">
                  <ChatLeftText className="me-2" />
                  {notes.length} Note{notes.length !== 1 ? 's' : ''}
                </Badge>
                <Badge bg="light" text="dark" className="px-3 py-2">
                  <Briefcase className="me-1" />
                  Project ID: {projectId?.substring(0, 12)}...
                </Badge>
              </div>
            </div>
            
            <div>
              <Button
                variant="primary"
                onClick={fetchNotes}
                className="d-flex align-items-center"
              >
                <ArrowClockwise className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* PROJECT INFO - Simplified without project info */}
          <Card className="mb-4 border-0 shadow-sm bg-light">
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="d-flex align-items-center mb-2">
                    <Briefcase className="me-3 text-primary" />
                    <div>
                      <h6 className="mb-0">Project Notes</h6>
                      <small className="text-muted">
                        All notes for project: {projectId?.substring(0, 16)}...
                      </small>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center mb-2">
                    <Calendar className="me-3 text-primary" />
                    <div>
                      <h6 className="mb-0">
                        {notes.length > 0 
                          ? `Latest: ${getTimeAgo(notes[0]?.createdAt)}` 
                          : 'No notes yet'
                        }
                      </h6>
                      <small className="text-muted">
                        Total: {notes.length} note{notes.length !== 1 ? 's' : ''}
                      </small>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* FILTERS & SEARCH */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3">
                <Col md={8}>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search notes by content or author..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </Col>
                
                <Col md={2}>
                  <InputGroup>
                    <InputGroup.Text>
                      <SortAlphaDown />
                    </InputGroup.Text>
                    <Form.Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="date">Sort by Date</option>
                      <option value="author">Sort by Author</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
                
                <Col md={2}>
                  <Button
                    variant={sortOrder === 'desc' ? 'primary' : 'secondary'}
                    onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                    className="w-100"
                  >
                    {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* NOTES LIST */}
      {filteredNotes.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FileEarmarkText size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Notes Found</h4>
            <p className="text-muted mb-4">
              {searchTerm
                ? 'No notes match your search criteria'
                : 'No notes have been added to this project yet'}
            </p>
            {searchTerm && (
              <Button 
                variant="outline-primary" 
                onClick={() => setSearchTerm('')}
              >
                Clear Search
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="p-4">
              <div className="row g-4">
                {filteredNotes.map((note, index) => (
                  <div key={note._id} className="col-12">
                    <Card className="border-0 shadow-sm mb-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div className="flex-grow-1">
                            {/* Author Info */}
                            <div className="d-flex align-items-center mb-3">
                              <div className="me-3">
                                <div 
                                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                  style={{ width: 50, height: 50 }}
                                  title={note.createdBy?.name}
                                >
                                  <span className="fw-bold">{getInitials(note.createdBy?.name)}</span>
                                </div>
                              </div>
                              <div>
                                <h6 className="mb-0">{note.createdBy?.name}</h6>
                                <small className="text-muted d-flex align-items-center">
                                  <Envelope size={12} className="me-1" />
                                  {note.createdBy?.email}
                                </small>
                                <small className="text-muted d-flex align-items-center mt-1">
                                  <Clock size={12} className="me-1" />
                                  {getTimeAgo(note.createdAt)} • {formatDateTime(note.createdAt)}
                                </small>
                              </div>
                            </div>
                            
                            {/* Note Content */}
                            <div className="mb-3">
                              <Card className="bg-light border-0">
                                <Card.Body>
                                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                    {note.note}
                                  </p>
                                </Card.Body>
                              </Card>
                            </div>
                            
                            {/* Note Metadata */}
                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                Note ID: {note._id?.substring(0, 8)}...
                              </small>
                              <Badge bg="light" text="dark">
                                #{index + 1}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
            
            {/* SUMMARY */}
            <div className="p-3 bg-light border-top">
              <Row>
                <Col md={6}>
                  <div className="small text-muted">
                    Showing {filteredNotes.length} of {notes.length} notes
                    {searchTerm && ` matching "${searchTerm}"`}
                  </div>
                </Col>
                <Col md={6} className="text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <div className="me-3">
                      <small className="text-muted">Sorted by:</small>
                      <Badge bg="info" className="ms-2">
                        {sortBy === 'date' ? 'Date' : 'Author'} ({sortOrder === 'desc' ? 'Newest First' : 'Oldest First'})
                      </Badge>
                    </div>
                    {notes.length > 0 && (
                      <small className="text-muted">
                        Latest note: {getTimeAgo(notes[0].createdAt)}
                      </small>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* NO DATA INFO */}
      {notes.length === 0 && !loading && (
        <Alert variant="info" className="mt-4">
          <div className="d-flex align-items-center">
            <FileEarmarkText className="me-3" size={24} />
            <div>
              <h6 className="mb-1">No Notes Available</h6>
              <p className="mb-0">This project doesn't have any notes yet. Notes can be added through the main application interface.</p>
            </div>
          </div>
        </Alert>
      )}
    </Container>
  );
};

export default Notes;